import BaseService from './BaseService'
import { STORE_BUILDER_DRAFT_STORAGE_KEY } from '@/config/storeBuilder'
import { cloneBuilderState } from '@/utils/storePageDocument'

/** Merchant store builder APIs (`/api/store/builder/*`). */
class StoreBuilderService extends BaseService {
  constructor () {
    super('/store/builder')
  }

  /** @returns {Promise<{ document, site_style, theme_preset, version } | null>} */
  async getDraft (pageType = 'home') {
    try {
      return await this.customRequest('get', `/draft/${pageType}`)
    } catch (error) {
      if (error?.response?.status === 404 || error?.response?.status === 501) {
        return null
      }
      throw error
    }
  }

  /**
   * @param {object} payload
   * @param {object} payload.document
   * @param {object} payload.site_style
   * @param {string} payload.theme_preset
   * @param {number} [payload.expected_version]
   */
  async saveDraft (pageType = 'home', payload) {
    try {
      return await this.customRequest('put', `/draft/${pageType}`, payload)
    } catch (error) {
      if (error?.response?.status === 404 || error?.response?.status === 501) {
        return this.saveDraftLocal(payload)
      }
      throw error
    }
  }

  /** @returns {Promise<{ savedLocally: true, version: number }>} */
  saveDraftLocal (payload) {
    const snapshot = cloneBuilderState({
      ...payload,
      saved_at: new Date().toISOString(),
      version: payload.expected_version ?? payload.version ?? 1,
    })
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORE_BUILDER_DRAFT_STORAGE_KEY, JSON.stringify(snapshot))
    }
    return Promise.resolve({ savedLocally: true, version: snapshot.version })
  }

  loadDraftLocal () {
    if (typeof localStorage === 'undefined') return null
    try {
      const raw = localStorage.getItem(STORE_BUILDER_DRAFT_STORAGE_KEY)
      if (!raw) return null
      return JSON.parse(raw)
    } catch {
      return null
    }
  }

  async getGlobals () {
    return this.customRequest('get', '/draft/globals')
  }

  async saveGlobals (payload) {
    return this.customRequest('put', '/draft/globals', payload)
  }

  async publish () {
    return this.customRequest('post', '/publish')
  }

  async createPreviewToken () {
    return this.customRequest('post', '/preview-token')
  }

  async listReleases () {
    const res = await this.customRequest('get', '/releases')
    return res?.data ?? []
  }

  async uploadSectionImage (imageFile) {
    if (!(imageFile instanceof File)) {
      throw new Error('Image must be a File object')
    }
    const formData = new FormData()
    formData.append('image', imageFile)

    return this.customRequest('post', '/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  /** Store-visible catalog rows for featured-products picker (`visible_on_store` + `store_slug`). */
  async listCatalogProducts ({ q, ids, limit } = {}) {
    const params = {}
    if (q) params.q = q
    if (ids) params.ids = ids
    if (limit) params.limit = limit
    const res = await this.customRequest('get', '/catalog-products', null, { params })
    return res?.data ?? []
  }

  async restore (releaseId) {
    return this.customRequest('post', '/restore', { release_id: releaseId })
  }

  async listContentPages () {
    const res = await this.customRequest('get', '/content-pages')
    return res?.data ?? []
  }

  async createContentPage ({ title, slug }) {
    return this.customRequest('post', '/content-pages', { title, slug })
  }

  async duplicateContentPage (slug) {
    return this.customRequest('post', `/content-pages/${encodeURIComponent(slug)}/duplicate`)
  }

  async deleteContentPage (slug) {
    return this.customRequest('delete', `/content-pages/${encodeURIComponent(slug)}`)
  }

  async getContentDraft (slug) {
    return this.customRequest('get', `/draft/content/${encodeURIComponent(slug)}`)
  }

  async saveContentDraft (slug, payload) {
    return this.customRequest('put', `/draft/content/${encodeURIComponent(slug)}`, payload)
  }
}

export const storeBuilderService = new StoreBuilderService()
